import React, { useState, useEffect } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Autocomplete,
  TextField,
  Skeleton,
} from "@mui/material";
import { useSidebar } from "../../components/SidebarContext";
import { getApiUrl, getApiHeaders } from "../../config/api";
import { auth } from "../../config/firebaseConfig";

const TotalItemsReported = ({ isTabView }) => {
  // State for search query
  const [searchQuery, setSearchQuery] = useState("");
  const [totalItemsData, setTotalItemsData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;
      if (!user) return;
      
      const token = await user.getIdToken();
      const response = await fetch(getApiUrl("found-item/list"), {
        headers: getApiHeaders({ Authorization: `Bearer ${token}` })
      });
      
      if (!response.ok) throw new Error("Failed to fetch items");
      
      const data = await response.json();
      const formattedData = (data.items || []).map(item => ({
        id: item.id?.toString() || "N/A",
        itemName: item.item_name || "Unknown",
        dateReceived: item.date_found ? new Date(item.date_found).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : "N/A",
        status: item.item_condition || "Available",
        action: "Update",
      }));
      
      setTotalItemsData(formattedData);
    } catch (error) {
      console.error("Error fetching items:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter the items based on the search query
  const filteredItems = totalItemsData.filter((item) =>
    item.itemName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const { isSidebarOpen } = useSidebar();
  const isMobile = /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth <= 600;

  // Tab view: only render table and footer
  if (isTabView) {
    return (
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <TableContainer sx={{ flex: 1, bgcolor: "#fbffe8", borderRadius: "15px", border: "2px solid #516d4f", overflow: "auto" }}>
          <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ 
                  bgcolor: "#fbffe8", 
                  borderRight: "1px solid rgba(81, 109, 79, 0.5)", 
                  borderBottom: "1px solid rgba(81, 109, 79, 1)", 
                  fontWeight: 500, 
                  color: "#516d4f", 
                  fontSize: { xs: "0.75rem", sm: "1rem", md: "1.25rem", lg: "1.5rem" },
                  position: "sticky", 
                  top: 0, 
                  zIndex: 1,
                  width: "15%",
                  padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" }
                }}>Item ID</TableCell>
                <TableCell sx={{ 
                  bgcolor: "#fbffe8", 
                  borderRight: "1px solid rgba(81, 109, 79, 0.5)", 
                  borderBottom: "1px solid rgba(81, 109, 79, 1)", 
                  fontWeight: 500, 
                  color: "#516d4f", 
                  fontSize: { xs: "0.75rem", sm: "1rem", md: "1.25rem", lg: "1.5rem" },
                  position: "sticky", 
                  top: 0, 
                  zIndex: 1,
                  width: "40%",
                  padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" }
                }}>Item Name</TableCell>
                <TableCell sx={{ 
                  bgcolor: "#fbffe8", 
                  borderRight: "1px solid rgba(81, 109, 79, 0.5)", 
                  borderBottom: "1px solid rgba(81, 109, 79, 1)", 
                  fontWeight: 500, 
                  color: "#516d4f", 
                  fontSize: { xs: "0.75rem", sm: "1rem", md: "1.25rem", lg: "1.5rem" },
                  position: "sticky", 
                  top: 0, 
                  zIndex: 1,
                  width: "25%",
                  padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" }
                }}>Date Received</TableCell>
                <TableCell sx={{ 
                  bgcolor: "#fbffe8", 
                  borderBottom: "1px solid rgba(81, 109, 79, 1)", 
                  fontWeight: 500, 
                  color: "#516d4f", 
                  fontSize: { xs: "0.75rem", sm: "1rem", md: "1.25rem", lg: "1.5rem" },
                  position: "sticky", 
                  top: 0, 
                  zIndex: 1,
                  width: "20%",
                  padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" }
                }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell sx={{ borderRight: "1px solid rgba(81, 109, 79, 0.5)", padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}><Skeleton variant="text" width="80%" height={30} /></TableCell>
                    <TableCell sx={{ borderRight: "1px solid rgba(81, 109, 79, 0.5)", padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}><Skeleton variant="text" width="80%" height={30} /></TableCell>
                    <TableCell sx={{ borderRight: "1px solid rgba(81, 109, 79, 0.5)", padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}><Skeleton variant="text" width="80%" height={30} /></TableCell>
                    <TableCell sx={{ padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" } }}><Skeleton variant="text" width="80%" height={30} /></TableCell>
                  </TableRow>
                ))
              ) : (
                filteredItems.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell sx={{ 
                      borderRight: "1px solid rgba(81, 109, 79, 0.5)", 
                      color: "#516d4f", 
                      fontSize: { xs: "0.7rem", sm: "0.9rem", md: "1.1rem", lg: "1.5rem" },
                      padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" },
                      wordBreak: "break-word"
                    }}>{item.id}</TableCell>
                    <TableCell sx={{ 
                      borderRight: "1px solid rgba(81, 109, 79, 0.5)", 
                      color: "#516d4f", 
                      fontSize: { xs: "0.7rem", sm: "0.9rem", md: "1.1rem", lg: "1.5rem" },
                      padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" },
                      wordBreak: "break-word"
                    }}>{item.itemName}</TableCell>
                    <TableCell sx={{ 
                      borderRight: "1px solid rgba(81, 109, 79, 0.5)", 
                      color: "#516d4f", 
                      fontSize: { xs: "0.7rem", sm: "0.9rem", md: "1.1rem", lg: "1.5rem" },
                      padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" },
                      wordBreak: "break-word"
                    }}>{item.dateReceived}</TableCell>
                    <TableCell sx={{ 
                      color: "#516d4f", 
                      fontSize: { xs: "0.7rem", sm: "0.9rem", md: "1.1rem", lg: "1.5rem" },
                      padding: { xs: "8px 4px", sm: "12px 8px", md: "16px" },
                      wordBreak: "break-word"
                    }}>{item.status}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ 
          bgcolor: "#516d4f", 
          borderRadius: "25px", 
          p: { xs: 1, sm: 1.5 }, 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          mt: 3 
        }}>
          <Typography 
            variant="h4" 
            fontWeight="600" 
            color="#f6ffc9" 
            sx={{ 
              ml: { xs: 1, sm: 2 },
              fontSize: { xs: "1rem", sm: "1.5rem", md: "2rem", lg: "2.125rem" }
            }}
          >
            Total Items Available
          </Typography>
          <Typography 
            variant="h3" 
            fontWeight="600" 
            color="#f6ffc9" 
            sx={{ 
              mr: { xs: 1, sm: 2 },
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem", lg: "3rem" }
            }}
          >
            {filteredItems.length}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Full page view (original)
  return (
    <Box
      sx={{
  transition: "margin-left 0.3s cubic-bezier(0.4,0,0.2,1), max-width 0.3s cubic-bezier(0.4,0,0.2,1)",
        marginLeft: isSidebarOpen ? (isMobile ? "200px" : "300px") : 0,
        bgcolor: "#fbffe8",
        display: "flex",
        justifyContent: "center",
        width: "100%",
        maxWidth: isSidebarOpen ? (isMobile ? "calc(100vw - 200px)" : "calc(100vw - 300px)") : "100vw",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          bgcolor: "#fbffe8",
          width: "100%",
          maxWidth: 1920,
          position: "relative",
          height: "100vh",
          p: 2,
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton sx={{ mr: 2 }}>
            <MenuIcon sx={{ fontSize: 36, color: "#516d4f" }} />
          </IconButton>
          <Typography variant="h5" fontWeight="bold" color="#516d4f">
            UFind
          </Typography>
        </Box>

        {/* Title and Search Bar */}
        <Box sx={{ px: 2, py: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            {/* Title */}
            <Typography variant="h4" fontWeight="bold" color="#516d4f">
              Total Items Report
            </Typography>

            {/* Search Bar */}
            <Autocomplete
              options={[]} // No options for now
              sx={{
                width: 320,
                "& .MuiInputBase-root": {
                  bgcolor: "rgba(81, 109, 79, 0.6)", // Background color
                  borderRadius: "30px", // Rounded corners
                  color: "#f6ffc9", // Text color
                },
              }}
              value={searchQuery}
              onChange={(e, newValue) => setSearchQuery(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search Items"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <SearchIcon sx={{ color: "#f6ffc9", ml: 1, mr: 1 }} />
                    ),
                  }}
                />
              )}
              popupIcon={
                <ArrowDropDownIcon sx={{ color: "#f6ffc9", fontSize: 40 }} />
              }
            />
          </Box>
        </Box>

        {/* Main Content */}
        <Paper
          elevation={4}
          sx={{
            bgcolor: "#e1ecc8",
            borderRadius: "20px",
            p: 2,
            mb: 2,
            height: "calc(100vh - 200px)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Table */}
          <TableContainer
            component={Paper}
            sx={{
              bgcolor: "#fbffe8",
              borderRadius: "20px",
              border: "2px solid #516d4f",
              flexGrow: 1, // Allows the table to expand while leaving space for the footer
              overflow: "auto",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      bgcolor: "#fbffe8",
                      borderRight: "1px solid rgba(81, 109, 79, 0.5)",
                      borderBottom: "1px solid rgba(81, 109, 79, 1)",
                      fontWeight: 500,
                      color: "#516d4f",
                      fontSize: "1.5rem",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    Item ID
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: "#fbffe8",
                      borderRight: "1px solid rgba(81, 109, 79, 0.5)",
                      borderBottom: "1px solid rgba(81, 109, 79, 1)",
                      fontWeight: 500,
                      color: "#516d4f",
                      fontSize: "1.5rem",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    Item Name
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: "#fbffe8",
                      borderRight: "1px solid rgba(81, 109, 79, 0.5)",
                      borderBottom: "1px solid rgba(81, 109, 79, 1)",
                      fontWeight: 500,
                      color: "#516d4f",
                      fontSize: "1.5rem",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    Date Received
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: "#fbffe8",
                      borderBottom: "1px solid rgba(81, 109, 79, 1)",
                      fontWeight: 500,
                      color: "#516d4f",
                      fontSize: "1.5rem",
                      position: "sticky",
                      top: 0,
                      zIndex: 1,
                    }}
                  >
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredItems.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell
                      sx={{
                        borderRight: "1px solid rgba(81, 109, 79, 0.5)",
                        color: "#516d4f",
                        fontSize: "1.5rem",
                      }}
                    >
                      {item.id}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: "1px solid rgba(81, 109, 79, 0.5)",
                        color: "#516d4f",
                        fontSize: "1.5rem",
                      }}
                    >
                      {item.itemName}
                    </TableCell>
                    <TableCell
                      sx={{
                        borderRight: "1px solid rgba(81, 109, 79, 0.5)",
                        color: "#516d4f",
                        fontSize: "1.5rem",
                      }}
                    >
                      {item.dateReceived}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#516d4f",
                        fontSize: "1.5rem",
                      }}
                    >
                      {item.status}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer Section */}
          <Box
            sx={{
              bgcolor: "#516d4f",
              borderRadius: "25px",
              p: 1.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 3, // Space between the table and the footer
            }}
          >
            <Typography
              variant="h4"
              fontWeight="600"
              color="#f6ffc9"
              sx={{ ml: 2 }}
            >
              Total Items Available
            </Typography>
            <Typography
              variant="h3"
              fontWeight="600"
              color="#f6ffc9"
              sx={{ mr: 2 }}
            >
              {filteredItems.length}
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default TotalItemsReported;
